import type { FC, FormEvent, ChangeEvent } from 'react';
import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import type { AppDispatch } from '../../../../../../../store/store';
import { setUser } from '../../../../../../../store/AuthSlice';
import { getCsrfToken } from '../../../../../../../utils/helpers';
import { maskDobInput, normalizeDobFromServer } from '../address.helpers';
import type { UserProfile } from './userData.types';
import { coerceUserProfile } from './userData.types';
import './personalDataPopupContent.scss';

type Props = {
    user: UserProfile;
    onClose: () => void;
};
type FieldKey = 'firstName' | 'lastName' | 'dateOfBirth' | 'phoneNumber' | 'email';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DOB_COMPLETE_RE = /^\d{2}\.\d{2}\.\d{4}$/;
const isDobComplete = (v: string) => DOB_COMPLETE_RE.test(String(v || '').trim());
const DOB_PARTS_RE = /^(\d{2})\.(\d{2})\.(\d{4})$/;
const startOfTodayUtc = (): Date => {
    const now = new Date();
    return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
};
const subtractYearsUtc = (dateUtc: Date, years: number): Date => {
    const d = new Date(dateUtc.getTime());
    d.setUTCFullYear(d.getUTCFullYear() - years);
    return d;
};
const validateDobIfProvided = (input: string): string | null => {
    const raw = String(input || '').trim();
    if (!raw) return null;
    if (!isDobComplete(raw)) return 'Формат: ДД.ММ.ГГГГ';
    const m = raw.match(DOB_PARTS_RE);
    if (!m) return 'Формат: ДД.ММ.ГГГГ';
    const dd = Number(m[1]);
    const mm = Number(m[2]);
    const yyyy = Number(m[3]);
    if (!Number.isFinite(dd) || !Number.isFinite(mm) || !Number.isFinite(yyyy)) return 'Некорректная дата';
    if (yyyy < 1900) return 'Слишком ранний год';
    if (mm < 1 || mm > 12) return 'Месяц должен быть 01..12';
    if (dd < 1 || dd > 31) return 'День должен быть 01..31';
    const dtUtc = new Date(Date.UTC(yyyy, mm - 1, dd));
    if (dtUtc.getUTCFullYear() !== yyyy || dtUtc.getUTCMonth() !== mm - 1 || dtUtc.getUTCDate() !== dd) {
        return 'Такой даты не существует';
    }
    const todayUtc = startOfTodayUtc();
    if (dtUtc.getTime() > todayUtc.getTime()) return 'Некорректный возраст';
    const minAllowed = subtractYearsUtc(todayUtc, 120);
    if (dtUtc.getTime() < minAllowed.getTime()) return 'Некорректный возраст';
    const maxAllowed = subtractYearsUtc(todayUtc, 12);
    if (dtUtc.getTime() > maxAllowed.getTime()) return 'Некорректный возраст';
    return null;
};
const digitsOnly = (v: string): string => v.replace(/\D/g, '');
export const formatRuPhoneParensFromE164 = (phone: string): string => {
    const d = digitsOnly(phone);
    const national10 = d.startsWith('7') ? d.slice(1, 11) : d.slice(0, 10);
    if (national10.length !== 10) return phone.trim() ? phone.trim() : '+7 (000) 000-00-00';
    const a = national10.slice(0, 3);
    const b = national10.slice(3, 6);
    const c = national10.slice(6, 8);
    const e = national10.slice(8, 10);
    return `+7 (${a}) ${b}-${c}-${e}`;
};
function normalizeUserAddressLikeLegacy(u: UserProfile): UserProfile {
    const anyU = u as any;
    let changed = false;
    const next: any = { ...anyU };
    const hasContainer = next.address && typeof next.address === 'object' && !Array.isArray(next.address);
    const hasLegacyStruct = !!next.addressStruct;
    const hasLegacyRaw = typeof next.addressRaw === 'string' && next.addressRaw.trim().length > 0;
    if (hasLegacyStruct || hasLegacyRaw) {
        const struct = hasLegacyStruct ? next.addressStruct : undefined;
        const raw = hasLegacyRaw ? String(next.addressRaw).trim() : undefined;
        if (struct || raw) {
            next.address = { raw: raw || (hasContainer ? next.address.raw : ''), struct: struct || (hasContainer ? next.address.struct : null) };
            changed = true;
        } else {
            next.address = null;
            changed = true;
        }
        if (hasLegacyStruct) {
            delete next.addressStruct;
            changed = true;
        }
        if (next.addressRaw !== undefined) {
            delete next.addressRaw;
            changed = true;
        }
    }
    if (hasContainer) {
        const raw = typeof next.address.raw === 'string' ? next.address.raw.trim() : '';
        const struct = next.address.struct && typeof next.address.struct === 'object' ? next.address.struct : null;
        if (!raw && !struct) {
            next.address = null;
            changed = true;
        } else {
            next.address = { raw: raw || '', struct };
            changed = true;
        }
    }
    return changed ? (next as UserProfile) : u;
}
const normalizeSpaces = (v: string): string => v.replace(/\s+/g, ' ').trim();

export const PersonalDataPopupContent: FC<Props> = ({ user, onClose }) => {
    const dispatch = useDispatch<AppDispatch>();
    const abortRef = useRef<AbortController | null>(null);
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [dateOfBirth, setDateOfBirth] = useState('');
    const [email, setEmail] = useState('');
    const [promoOptIn, setPromoOptIn] = useState(true);
    const promoOptInInitialRef = useRef<boolean>(true);
    const promoOptInHadServerValueRef = useRef<boolean>(false);
    const phoneFormatted = useMemo(() => formatRuPhoneParensFromE164(String(user.phoneNumber || '')), [user.phoneNumber]);
    const serverDobMasked = useMemo(() => normalizeDobFromServer(String(user?.dateOfBirth?.date || '')).trim(), [user?.dateOfBirth?.date]);
    const canEditDob = !serverDobMasked;
    const [errors, setErrors] = useState<Partial<Record<FieldKey, string>>>({});
    const [status, setStatus] = useState<string>('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const normalizedUser = normalizeUserAddressLikeLegacy(user);
        setFirstName(String(normalizedUser.firstName || '').trim());
        setLastName(String(normalizedUser.lastName || '').trim());
        setDateOfBirth(normalizeDobFromServer(String(normalizedUser?.dateOfBirth?.date || '')));
        setEmail(String(normalizedUser.email || '').trim());
        const discounts = normalizedUser.marketing?.discounts_and_promotions;
        const hasServerValue = typeof discounts === 'boolean';
        const initial = hasServerValue ? discounts : true;
        promoOptInHadServerValueRef.current = hasServerValue;
        promoOptInInitialRef.current = initial;
        setPromoOptIn(initial);
        setErrors({});
        setStatus('');
        setSaving(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    useEffect(() => {
        return () => {
            abortRef.current?.abort();
        };
    }, []);

    const clearFieldError = useCallback((key: FieldKey) => {
        setErrors((prev) => {
            if (!prev[key]) return prev;
            const next = { ...prev };
            delete next[key];
            return next;
        });
    }, []);

    const validate = (): boolean => {
        const next: Partial<Record<FieldKey, string>> = {};
        if (!firstName.trim()) next.firstName = 'Укажите имя';
        if (!lastName.trim()) next.lastName = 'Укажите фамилию';
        const phoneRaw = String(user.phoneNumber || '').trim();
        if (!phoneRaw) next.phoneNumber = 'Номер телефона обязателен';
        const dobMasked = maskDobInput(dateOfBirth).trim();
        const dobErr = canEditDob ? validateDobIfProvided(dobMasked) : null;
        if (dobErr) next.dateOfBirth = dobErr;
        const emailSanitized = normalizeSpaces(email);
        if (!EMAIL_RE.test(emailSanitized)) next.email = 'Некорректный e-mail';
        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const onChangeFirstName = (e: ChangeEvent<HTMLInputElement>) => {
        setFirstName(e.target.value);
        clearFieldError('firstName');
    };

    const onChangeLastName = (e: ChangeEvent<HTMLInputElement>) => {
        setLastName(e.target.value);
        clearFieldError('lastName');
    };

    const onChangeEmail = (e: ChangeEvent<HTMLInputElement>) => {
        setEmail(e.target.value);
        clearFieldError('email');
    };

    const onChangeDob = (e: ChangeEvent<HTMLInputElement>) => {
        const masked = maskDobInput(e.target.value);
        setDateOfBirth(masked);
        clearFieldError('dateOfBirth');
    };

    const onBlurDob = () => {
        const dobMasked = maskDobInput(dateOfBirth).trim();
        const err = canEditDob ? validateDobIfProvided(dobMasked) : null;
        setErrors((p) => ({ ...p, dateOfBirth: err || undefined }));
    };

    const onSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setStatus('');
        if (!validate()) return;
        abortRef.current?.abort();
        const ctrl = new AbortController();
        abortRef.current = ctrl;
        setSaving(true);
        try {
            const csrfToken = await getCsrfToken();
            const safeFirstName = normalizeSpaces(firstName);
            const safeLastName = normalizeSpaces(lastName);
            const safeEmail = normalizeSpaces(email);
            const safeDob = maskDobInput(dateOfBirth).trim();
            const body: {
                lastName: string;
                firstName: string;
                middleName: string;
                email: string;
                address: UserProfile['address'] | null;
                dateOfBirth?: string;
                marketing?: { discounts_and_promotions: boolean };
            } = {
                lastName: safeLastName,
                firstName: safeFirstName,
                middleName: normalizeSpaces(String(user.middleName || '')),
                email: safeEmail,
                address: (normalizeUserAddressLikeLegacy(user).address ?? null) as UserProfile['address'] | null,
            };
            const shouldSendMarketing = !promoOptInHadServerValueRef.current || promoOptIn !== promoOptInInitialRef.current;
            if (shouldSendMarketing) {
                body.marketing = { discounts_and_promotions: !!promoOptIn };
            }
            if (canEditDob && safeDob) body.dateOfBirth = safeDob;
            const r = await fetch(`${process.env.REACT_APP_SERVER_URL}/auth/user`, {
                method: 'PATCH',
                credentials: 'include',
                signal: ctrl.signal,
                headers: {
                    'Content-Type': 'application/json',
                    'csrf-token': csrfToken,
                },
                body: JSON.stringify(body),
            });
            if (!r.ok) {
                const data = (await r.json().catch(() => null)) as unknown;
                const msg =
                    typeof (data as { error?: unknown }).error === 'string'
                        ? String((data as { error?: unknown }).error)
                        : typeof (data as { message?: unknown }).message === 'string'
                          ? String((data as { message?: unknown }).message)
                          : 'Не удалось сохранить изменения';
                setStatus(msg);
                return;
            }
            const data = (await r.json().catch(() => ({}))) as unknown;
            const nextUser = coerceUserProfile(data);
            const normalizedNext = normalizeUserAddressLikeLegacy(nextUser);
            (normalizedNext as any).addresses = Array.isArray((user as any)?.addresses) ? (user as any).addresses : [];
            const prevPrimaryComment = (user as any)?.address?.comment;
            if ((normalizedNext as any)?.address && typeof (normalizedNext as any).address === 'object') {
                if (typeof (normalizedNext as any).address.comment !== 'string' && typeof prevPrimaryComment === 'string') {
                    (normalizedNext as any).address = { ...(normalizedNext as any).address, comment: prevPrimaryComment };
                }
            }

            dispatch(setUser(normalizedNext));
            onClose();
        } catch (err: unknown) {
            const isAbort =
                typeof err === 'object' && err !== null && 'name' in err && typeof (err as { name?: unknown }).name === 'string'
                    ? (err as { name: string }).name === 'AbortError'
                    : false;
            if (!isAbort) setStatus('Ошибка сети/сервера при сохранении');
        } finally {
            setSaving(false);
        }
    };

    return (
        <form className="personalDataPopup" onSubmit={onSubmit} noValidate>
            <div className="personalDataPopup__head">
                <h1 className="personalDataPopup__title">Личные данные</h1>
                <button className="personalDataPopup__close" type="button" onClick={onClose} aria-label="Закрыть">
                    <img className="personalDataPopup__closeIcon" src="/icons/new/close.svg" alt="" aria-hidden="true" />
                </button>
            </div>
            <p className="personalDataPopup__subtitle">Заполните личные данные для быстрого оформления заказа</p>
            <div className="personalDataPopup__fields">
                <div className="personalDataPopup__field">
                    <label className="personalDataPopup__label" htmlFor="pdp-firstName">
                        Имя<span className="personalDataPopup__req">*</span>
                    </label>
                    <input
                        id="pdp-firstName"
                        className={`personalDataPopup__input${errors.firstName ? ' personalDataPopup__input_error' : ''}`}
                        value={firstName}
                        placeholder="Имя*"
                        onChange={onChangeFirstName}
                        maxLength={25}
                        autoComplete="given-name"
                        aria-invalid={errors.firstName ? true : undefined}
                    />
                    {errors.firstName ? <div className="personalDataPopup__error">{errors.firstName}</div> : null}
                </div>
                <div className="personalDataPopup__field">
                    <label className="personalDataPopup__label" htmlFor="pdp-lastName">
                        Фамилия<span className="personalDataPopup__req">*</span>
                    </label>
                    <input
                        id="pdp-lastName"
                        className={`personalDataPopup__input${errors.lastName ? ' personalDataPopup__input_error' : ''}`}
                        value={lastName}
                        placeholder="Фамилия*"
                        onChange={onChangeLastName}
                        maxLength={55}
                        autoComplete="family-name"
                        aria-invalid={errors.lastName ? true : undefined}
                    />
                    {errors.lastName ? <div className="personalDataPopup__error">{errors.lastName}</div> : null}
                </div>
                <div className="personalDataPopup__field">
                    <label className="personalDataPopup__label" htmlFor="pdp-dob">
                        Дата рождения
                    </label>
                    <input
                        id="pdp-dob"
                        className={`personalDataPopup__input${errors.dateOfBirth ? ' personalDataPopup__input_error' : ''}`}
                        value={dateOfBirth}
                        onChange={onChangeDob}
                        onBlur={onBlurDob}
                        placeholder="Дата рождения"
                        maxLength={10}
                        inputMode="numeric"
                        // pattern="\d{2}\.\d{2}\.\d{4}"
                        pattern="\\d{2}\\.\\d{2}\\.\\d{4}"
                        aria-invalid={errors.dateOfBirth ? true : undefined}
                        disabled={!canEditDob}
                        readOnly={!canEditDob}
                    />
                    {errors.dateOfBirth ? <div className="personalDataPopup__error">{errors.dateOfBirth}</div> : null}
                    <div className="personalDataPopup__hint">Для получения персональной скидки ко дню рождения</div>
                </div>
                <div className="personalDataPopup__field">
                    <label className="personalDataPopup__label" htmlFor="pdp-phone">
                        Номер телефона<span className="personalDataPopup__req">*</span>
                    </label>
                    <input
                        id="pdp-phone"
                        className={`personalDataPopup__input${errors.phoneNumber ? ' personalDataPopup__input_error' : ''} personalDataPopup__input_disabled`}
                        value={phoneFormatted}
                        disabled
                        readOnly
                        aria-invalid={errors.phoneNumber ? true : undefined}
                    />
                    {errors.phoneNumber ? <div className="personalDataPopup__error">{errors.phoneNumber}</div> : null}
                </div>
                <div className="personalDataPopup__field">
                    <label className="personalDataPopup__label" htmlFor="pdp-email">
                        Электронная почта<span className="personalDataPopup__req">*</span>
                    </label>
                    <input
                        id="pdp-email"
                        type="email"
                        className={`personalDataPopup__input${errors.email ? ' personalDataPopup__input_error' : ''}`}
                        value={email}
                        onChange={onChangeEmail}
                        maxLength={55}
                        autoComplete="email"
                        placeholder="Электронная почта*"
                        aria-invalid={errors.email ? true : undefined}
                    />
                    {errors.email ? <div className="personalDataPopup__error">{errors.email}</div> : null}
                    <div className="personalDataPopup__hint">Для получения чеков и полезной информации</div>
                </div>
            </div>
            <div className="personalDataPopup__spacer" />
            <div className="personalDataPopup__footer">
                <label className="personalDataPopup__agree">
                    <input className="personalDataPopup__checkbox" type="checkbox" checked={promoOptIn} onChange={(e) => setPromoOptIn(e.target.checked)} />
                    <span className="personalDataPopup__checkboxBox" aria-hidden="true" />
                    <span className="personalDataPopup__agreeText">Хочу узнавать об эксклюзивных скидках и акциях</span>
                </label>
                {status ? (
                    <div className="personalDataPopup__status" role="alert" aria-live="polite">
                        {status}
                    </div>
                ) : null}
                <button className="personalDataPopup__submit" type="submit" disabled={saving} aria-disabled={saving}>
                    {saving ? 'Сохранение…' : 'Сохранить'}
                </button>
            </div>
        </form>
    );
};

export default PersonalDataPopupContent;
